/**
 * Shell configuration types for domain dashboard apps.
 * @package @plerity/dashboard-shell
 */

import type { SettingsTab, UserInfo } from "@plerity/dashboard-shared/components";
import type { AppSwitcherEntry, SidebarBrand } from "@plerity/dashboard-shared/layouts";
import type { NavItem, NavSection } from "@plerity/dashboard-shared/types";
import type React from "react";
import { resolveShellUrl } from "./utils/shellRegistry";

// SettingsTab is already re-exported from `./index.ts` for consumer ergonomics
// (`import type { SettingsTab } from "@plerity/dashboard-shell"`).

// ============================================================================
// Chat Config
// ============================================================================

/** Chat widget configuration for a shell */
export interface ShellChatConfig {
  /** Enable the chatbot in this shell (default: false) */
  enabled: boolean;
  /** Additional system prompt context for this shell's domain */
  systemContext?: string;
  /** Domain features the chatbot should know about */
  features?: string[];
  /** Override the chat API endpoint (defaults to VITE_CHAT_API_URL or /api/chat) */
  apiEndpoint?: string;
  /**
   * Rebind (e.g. `"j"`) or disable (`false`) the widget's global Cmd/Ctrl+<key>
   * toggle. Set `false` when the host app owns Ctrl+K (e.g. a command
   * palette) — this also hides the "Ctrl+K to toggle" hint. Default: `"k"`.
   */
  toggleShortcut?: string | false;
  /** Override (string) or hide (`false`) the panel's shortcut hint text. */
  shortcutHint?: string | false;
}

// ============================================================================
// Tester Notes Config
// ============================================================================

/**
 * Tester-notes widget configuration for a shell.
 *
 * The widget is opt-in per app — set `enabled: true` to advertise its
 * availability. The user still has to flip the `bottomRightWidget`
 * preference (Settings → Floating widget) to actually swap from the
 * Plerity Assistant to the Tester Notes capture.
 */
export interface ShellTesterNotesConfig {
  /** Whether tester notes is available in this shell (default: false) */
  enabled: boolean;
  /**
   * Override the dev-notes API endpoint. Defaults to `/api/dev-notes`
   * (same-origin proxy expected). In dev, point to
   * `http://localhost:8395/api/dev-notes`.
   */
  apiEndpoint?: string;
  /**
   * Override the `context_id` value sent to the backend. Defaults to
   * `web-<shellName>`, e.g. `web-crm`. Use a custom value when more than
   * one logical surface ships under the same shell name (e.g. portal-admin
   * vs portal-client).
   */
  contextId?: string;
}

// ============================================================================
// Route Module
// ============================================================================

/** A lazy-loaded route module within a shell */
export interface RouteModule {
  /** Route path (relative to shell root, e.g. "bookkeeping/overview") */
  path: string;
  /** Display label for sidebar navigation */
  label: string;
  /**
   * Lucide icon component. Eager OR lazy — both supported.
   *
   * Eager (default — synchronous render, included in main chunk):
   *   `import { Settings } from "lucide-react";`
   *   `{ icon: Settings }`
   *
   * Lazy (deferred chunk, recommended for non-primary routes):
   *   `const Settings = React.lazy(() => import("lucide-react/dist/esm/icons/settings"));`
   *   `{ icon: Settings }`
   * SidebarNav wraps the icon render in `<Suspense>` with a 16x16 placeholder
   * so layout doesn't shift while a lazy icon resolves. F-O2 of
   * agents/plans/apps/finance_app_quality_plan_2026-04-25.md (manager-dashboards).
   */
  icon?: React.ComponentType<{ className?: string }>;
  /**
   * Async import for React.lazy() code splitting.
   *
   * EITHER `loader` OR `external + deepLink` must be set (mutually exclusive).
   * External routes (external: true) skip routing entirely — they render as
   * `<DeepLink>` in the sidebar rather than registering a React Router `<Route>`.
   */
  loader?: () => Promise<{ default: React.ComponentType }>;
  /** Nested child routes */
  children?: RouteModule[];
  /** Whether to hide from sidebar (still routable) */
  hidden?: boolean;
  /** Static badge text/count for sidebar */
  badge?: string | number;
  /**
   * Live sidebar badge — a React hook returning the current badge value
   * (e.g. an unread count from `useApiQuery`). Runs inside the shell's
   * QueryClientProvider + AuthProvider, once per rendered nav item.
   * `null`/`undefined`/`0`/`""` hide the badge; numbers > 99 show `99+`;
   * a dot marks the item when the sidebar is collapsed. Wins over `badge`.
   *
   * @example
   * useBadge: () => useApiQuery<{ count: number }>(["inbox", "unread"], "/api/inbox/unread-count").data?.count
   */
  useBadge?: () => string | number | null | undefined;
  /**
   * Edge-to-edge route: no `<main>` padding, route owns scrolling (`<main>`
   * is a `flex flex-col overflow-hidden` box — give the route root
   * `flex-1 min-h-0`). Sidebar + header remain. Matched against this
   * route's `path` (params allowed, e.g. `"social/designs/:id"`).
   */
  fullBleed?: boolean;
  /**
   * Immersive route (implies `fullBleed`): hides the sidebar, header and the
   * floating assistant/tester-notes FAB. `--shell-header-height` becomes
   * `0px`. The route must render its own exit (e.g. "Back to designs").
   */
  hideChrome?: boolean;
  /** Index route — renders when parent path matches exactly */
  index?: boolean;
  /**
   * When true, this route renders in the sidebar as a cross-shell deep link
   * (`<DeepLink>`) instead of a react-router `<Link>`. The route is NOT
   * registered with the router — it navigates to another shell entirely.
   *
   * Must be combined with `deepLink: { kind, path }`.
   * Cannot be combined with `loader`.
   *
   * Phase 4 — CRM × Ops Consolidation (cross-link UX polish).
   */
  external?: boolean;
  /**
   * Deep-link target configuration for external routes.
   * `kind` is the target shell identifier; `path` is the path within that shell.
   *
   * @example
   * { external: true, deepLink: { kind: "ops", path: "/tasks" } }
   */
  deepLink?: {
    kind: import("./components/DeepLink").DeepLinkShell;
    path: string;
  };
}

// ============================================================================
// Sidebar Group
// ============================================================================

/** A logical sidebar section grouping routes */
export interface SidebarGroup {
  /** Section heading */
  title: string;
  /** Route modules in this section */
  routes: RouteModule[];
  /** Allow collapsing this section */
  collapsible?: boolean;
  /** Start expanded */
  defaultOpen?: boolean;
}

// ============================================================================
// Shell Config
// ============================================================================

/** Auth configuration for createShell(). When provided, wraps the shell in AuthProvider + AuthGuard. */
export interface ShellAuthConfig {
  /** Whether authentication is required (true = wrap in AuthGuard) */
  required: boolean;
  /**
   * Required product entitlement (e.g., "finance", "crm").
   *
   * - `string` — user must have this exact entitlement
   * - `string[]` — OR-semantics: user must have AT LEAST ONE of these
   *   (e.g., `["ops", "crm"]` admits users entitled to either product —
   *   used by the merged Ops + CRM shell)
   * - `[]` or omitted — no entitlement gate (auth-only)
   */
  entitlement?: string | string[];
  /** Portal login URL for unauthenticated redirects (default: PORTAL_URLS.LOGIN) */
  loginUrl?: string;
  /** Portal logout URL (default: PORTAL_URLS.LOGOUT) */
  logoutUrl?: string;
  /** Portal API base URL (default: PORTAL_URLS.API) */
  portalApiUrl?: string;
  /**
   * Public route paths that bypass `AuthGuard` even when `required: true`.
   *
   * Each entry matches the location pathname EXACTLY OR as a prefix
   * (`<path>/...`). Use for unauthenticated entry-points such as signup /
   * onboarding flows whose users do not yet have a portal account.
   *
   * The shell still wraps `AuthProvider` so `useAuth()` continues to work
   * and AuthGuard kicks in as soon as the user navigates to a non-public
   * route. Only the gate is conditional, never the provider.
   *
   * @example
   * publicPaths: ["/dgo/onboarding"]   // matches /dgo/onboarding and /dgo/onboarding/anything
   *
   * Added 2026-04-29 (dgo handoff Item: onboarding hydration failure fix).
   */
  publicPaths?: string[];
}

/** Configuration for a domain shell application */
export interface ShellConfig {
  /** Shell identifier (e.g. "finance", "crm", "platform") */
  name: string;
  /** Human-readable title shown in header */
  displayName: string;
  /**
   * Shorter header title shown below the `sm` breakpoint (<640px), where the
   * header can leave as little as ~50-60px for the title and a longer
   * `displayName` truncates to e.g. "S…" (social live QA, 2026-10-04 —
   * backlog shell-short-title-phones-2026-10-04-001). `displayName` is still
   * the ONLY accessible name (via `aria-label`) regardless of viewport — this
   * only changes what's visually shown at narrow widths. Omit for apps whose
   * `displayName` already fits comfortably (e.g. "SEO", "Data", "AI").
   *
   * @example
   * { displayName: "Social Media", shortTitle: "Social" }
   */
  shortTitle?: string;
  /** Sidebar navigation groups — if provided, used instead of auto-generating from routes */
  sidebarGroups?: SidebarGroup[];
  /** Flat route list — used when sidebarGroups is not provided */
  routes?: RouteModule[];
  /** Default route path to redirect "/" to (e.g. "/bookkeeping/overview") */
  defaultRoute?: string;
  /** Custom header actions (rendered right side of header bar) */
  headerActions?: React.ReactNode;
  /** Brand override for sidebar (defaults to Animas brand) */
  brand?: SidebarBrand | false;
  /** App switcher entries (defaults to standard Portal/CRM/Builder) */
  apps?: AppSwitcherEntry[] | false;
  /** Current app ID for app switcher active state */
  currentApp?: string;
  /** Router basename — must be set explicitly per shell */
  basename?: string;
  /**
   * Router flavour (default `"browser"` — classic BrowserRouter, unchanged).
   * Set `"data"` to mount a data router (`createBrowserRouter` + single splat
   * root route; all existing routes behave identically) so routes can use
   * `useShellNavigationBlocker()` / `<ShellUnsavedChangesPrompt>` to guard
   * in-app navigation with unsaved edits.
   */
  router?: "browser" | "data";
  /** Custom 404 page component */
  notFoundComponent?: React.ComponentType;
  /** Additional sidebar footer content */
  sidebarFooter?: React.ReactNode;
  /** Chat widget configuration (enables floating AI assistant) */
  chat?: ShellChatConfig;
  /**
   * Tester-notes widget configuration (enables the bug-report FAB
   * sibling to the assistant chat). Both share the same UI slot — the
   * user picks which one renders via Settings → Floating widget.
   */
  testerNotes?: ShellTesterNotesConfig;
  /**
   * Authenticated user info. When provided, the header renders a `UserMenu`
   * dropdown (avatar → identity + theme selector + settings link + sign out)
   * instead of the bare `ThemeToggle` button.
   */
  user?: UserInfo;
  /**
   * Client-side route for the full settings page (e.g. '/settings').
   * Shown as a "Settings" link inside the UserMenu dropdown.
   * Only used when `user` is also provided.
   */
  settingsRoute?: string;
  /**
   * Full URL to the security / password-change page (e.g.
   * 'https://app.animasmarketing.com/settings/security').
   * Rendered as an `<a href>` (not react-router Link) because it
   * typically navigates to the portal on a different domain.
   * Only used when `user` is also provided.
   */
  securityRoute?: string;
  /**
   * Called when the user activates "Sign out" in the UserMenu.
   * Omit to hide the sign-out action.
   * Only used when `user` is also provided.
   */
  onSignOut?: () => void;
  /**
   * Unified auth configuration. When provided:
   * - Wraps shell in `AuthProvider` (from @plerity/auth/react)
   * - When `auth.required: true`, wraps content in `AuthGuard`
   * - Adds `UserMenu` from auth state (overrides manual `user` prop)
   * - `onSignOut` wired to auth signOut automatically
   *
   * Omit for backward compat — shell renders without auth wrapper.
   */
  auth?: ShellAuthConfig;
  /**
   * Per-app settings configuration. When provided, createShell registers a
   * hidden settings route at `settingsRoute` (default `/settings`) that
   * renders the shared `UserSettingsPage` with these app-specific tabs
   * injected between Profile and the built-in Appearance tab.
   *
   * - `additionalTabs` — App-specific settings tabs (e.g. domain prefs).
   *   Use `SettingsTab[]` from `@plerity/dashboard-shared/components`.
   *
   * Omit if the app handles settings via its own route component.
   */
  settings?: {
    additionalTabs?: SettingsTab[];
    /**
     * `context_id` forwarded to the auto-included Tester Notes tab. When
     * omitted, defaults to `config.testerNotes.contextId` (or `web-<name>`
     * as last-resort) so the two never drift. Match the value passed to
     * `createShell({ testerNotes: { contextId } })`.
     */
    testerNotesContextId?: string;
    /**
     * Display name shown in the auto-included Assistant tab — e.g.
     * "Operations Assistant", "Finance Assistant". Defaults to
     * `<displayName> Assistant`.
     */
    assistantName?: string;
    /**
     * Built-in tabs to hide. Pass `'tester-notes'` to fully suppress the
     * shared Tester Notes tab even for super-admins (e.g. for a client-
     * facing shell that never wants the tab at all). The base set is:
     * `'profile' | 'notifications' | 'privacy' | 'tester-notes' | 'assistant'`.
     * `appearance` is always present.
     */
    disableBaseTabs?: Array<"profile" | "notifications" | "privacy" | "tester-notes" | "assistant">;
  };
}

// ============================================================================
// Helpers
// ============================================================================

/** Convert a RouteModule to a NavItem, recursively mapping visible children */
function routeToNavItem(route: RouteModule): NavItem {
  const item: NavItem = {
    label: route.label,
    path: route.path.startsWith("/") ? route.path : `/${route.path}`,
    icon: route.icon,
    badge: route.badge,
  };
  if (route.useBadge) item.useBadge = route.useBadge;

  // External deep-link routes: resolve the target URL from the shell registry
  // and set NavItem.href so SidebarNav renders a plain <a> instead of a NavLink.
  if (route.external && route.deepLink) {
    item.href = resolveShellUrl(route.deepLink.kind, route.deepLink.path, {
      hostname: typeof window !== "undefined" ? window.location.hostname : "localhost",
    });
  }

  // Map visible children (non-hidden) to NavItem children for sub-menu display
  const visibleChildren = route.children?.filter((c) => !c.hidden);
  if (visibleChildren && visibleChildren.length > 0) {
    item.children = visibleChildren.map(routeToNavItem);
  }

  return item;
}

/** Convert SidebarGroups to NavSection[] for DashboardLayout.
 *  Groups whose visible items list is empty (all routes hidden) are
 *  excluded so they don't render as empty collapsible section headers. */
export function sidebarGroupsToNavSections(groups: SidebarGroup[]): NavSection[] {
  return groups
    .map((group) => ({
      title: group.title,
      collapsible: group.collapsible,
      defaultOpen: group.defaultOpen,
      items: group.routes.filter((r) => !r.hidden).map(routeToNavItem),
    }))
    .filter((section) => section.items.length > 0);
}

/** Convert flat routes to NavSection[] (single section per group of routes) */
export function routesToNavSections(routes: RouteModule[], shellName: string): NavSection[] {
  const visibleRoutes = routes.filter((r) => !r.hidden);
  if (visibleRoutes.length === 0) return [];

  return [
    {
      title: shellName,
      items: visibleRoutes.map(routeToNavItem),
    },
  ];
}
